import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useTranslation } from 'react-i18next';
import {
  TMAI_MEMBERSHIP_FRAMEWORK_2022_03,
  candidacyClock,
  type CandidacyClock,
  type MembershipStage,
  type NetworkSubmission,
  type ReviewerDecision,
  type StageChange,
} from '@dosfilos/domain';
import { networkApi, type PreviousRound } from '../../lib/api/network.api';
import { errorText } from '../../lib/error-text';
import { useTenantStore } from '../../stores/tenant.store';
import { useUiStore } from '../../stores/ui.store';
import { formatDate } from '../../lib/i18n-format';

// ─── El expediente de una ronda (R1-PR6b) ───────────────────────────────────
//
// POR QUÉ UNA PÁGINA Y NO UN DIÁLOGO. Un paquete de candidatura trae 37 criterios
// y uno de membresía 29; el diálogo mostraba cuatro y ya cortaba el campo de
// comentarios. Pero el argumento de fondo no es el espacio:
//
//  · TIENE URL PROPIA. Un revisor puede pasarle el expediente a otro miembro de
//    la junta. En un proceso donde se DELIBERA, eso es funcional, no cosmético.
//  · El botón "atrás" del navegador vuelve a la bandeja en vez de cerrar todo.
//  · Entra el HISTORIAL de rondas anteriores, que en un diálogo no cabía — y sin
//    él, quien devuelve una ronda puede repetir una observación ya resuelta o
//    contradecir a un colega.
//  · Un diálogo comunica ligereza: se abre encima, se cierra con Escape. Aprobar
//    arranca un plazo normativo de DOS AÑOS. La forma debería decir eso.
//
// EL ID DE UN CRITERIO NO ES SU ENUNCIADO. La primera versión listaba
// `CAND_ACAD_COURSE_DESCRIPTIONS` y esperaba que el revisor supiera qué es. El
// marco tiene el texto completo en los dos idiomas; se resuelve acá. Si un id no
// está en el marco —una ronda de una versión anterior— se muestra el id, que es
// lo único honesto que queda.

const CRITERION = new Map(TMAI_MEMBERSHIP_FRAMEWORK_2022_03.criteria.map((c) => [c.id, c]));

const OUTCOME_ICON: Record<string, typeof CheckCircleIcon> = {
  met: CheckCircleIcon,
  not_met: ErrorOutlineIcon,
  no_data: HelpOutlineIcon,
  not_applicable: RemoveCircleOutlineIcon,
  partial: HelpOutlineIcon,
};

/** El color dice el ESTADO, y `no_data` NO es rojo: ausente no es incumplido. */
const OUTCOME_COLOR: Record<string, string> = {
  met: 'success.main',
  not_met: 'error.main',
  no_data: 'text.disabled',
  not_applicable: 'text.disabled',
  partial: 'warning.main',
};

export function SubmissionReviewPage() {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { submissionId = '' } = useParams();
  const { currentTenantId } = useTenantStore();
  const tenantId = currentTenantId ?? '';
  const lang = (useUiStore((st) => st.language) ?? 'es') as 'es' | 'en';

  const [submission, setSubmission] = useState<NetworkSubmission | null>(null);
  const [previous, setPrevious] = useState<PreviousRound[]>([]);
  const [clock, setClock] = useState<CandidacyClock | null>(null);
  const [observations, setObservations] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  /** El panel de decisión se ensancha para escribir cómodo.
   *
   *  DOS ANCHOS, NO UN DIVISOR ARRASTRABLE. Arrastrar exige estado continuo,
   *  persistir la preferencia, y manejo de teclado para que sea accesible — y a
   *  cambio da una precisión que nadie necesita: el revisor quiere "leer" o
   *  "escribir", no 437 píxeles. Dos estados dan el control sin la maquinaria. */
  const [wide, setWide] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId || !submissionId) return;
    try {
      const d = await networkApi.getSubmissionDetail({ tenantId, submissionId });
      setSubmission(d.submission);
      setPrevious(d.previous ?? []);
      const stage = d.achievedStage as MembershipStage | undefined;
      setClock(
        stage
          ? candidacyClock(stage, (d.stageHistory ?? []) as StageChange[], new Date().toISOString())
          : null,
      );
    } catch (e) {
      setLoadError(errorText(e));
    }
  }, [tenantId, submissionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const fmt = (iso: string) => formatDate(iso, { dateStyle: 'long' });

  const grouped = useMemo(() => {
    if (!submission) return [];
    const byCat = new Map<string, NetworkSubmission['packet']['criteria']>();
    for (const c of submission.packet.criteria) {
      const list = byCat.get(c.category) ?? [];
      list.push(c);
      byCat.set(c.category, list);
    }
    // Dentro de cada categoría, lo que EXIGE ATENCIÓN va arriba: incumplido,
    // luego sin datos, luego el resto. El revisor no debería tener que buscar.
    const weight: Record<string, number> = { not_met: 0, partial: 1, no_data: 2 };
    return [...byCat.entries()].map(([category, items]) => ({
      category,
      items: [...items].sort((a, b) => (weight[a.status] ?? 3) - (weight[b.status] ?? 3)),
    }));
  }, [submission]);

  async function decide(decision: ReviewerDecision) {
    setBusy(true);
    setError('');
    try {
      await networkApi.decideSubmission({ tenantId, submissionId, decision, observations });
      navigate('/red');
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <Box>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }
  if (!submission) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const p = submission.packet;
  const isOpen = submission.status === 'submitted';
  const isFormal = submission.submissionKind === 'formal';
  const gapCount = p.criteria.filter((c) => c.gating && c.status === 'not_met').length;

  return (
    // SIN PADDING PROPIO. `AppShell` ya envuelve cada página en `p: { xs: 2, sm: 3 }`;
    // agregar el nuestro encima duplicaba el margen y regalaba ~24px verticales en
    // una pantalla que muestra 41 criterios. El margen lo pone el shell; acá solo se
    // limita el ancho para que las líneas largas no crucen media pantalla.
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* CABECERA DE ANCHO COMPLETO. El nombre de la institución es contexto de
          TODA la página, no de la columna izquierda: metido adentro de una
          columna empujaba el contenido hacia abajo y dejaba la otra arrancando
          sin nada que la anclara. Acá arriba, las dos columnas empiezan parejas.
          Y "volver" es un enlace en la misma línea del recorrido, no una fila
          entera para un solo botón. */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/red')}
              color="inherit"
              sx={{ ml: -1, minWidth: 0 }}
            >
              {t('network.review.backToInbox')}
            </Button>
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', letterSpacing: '0.08em', lineHeight: 1 }}
            >
              {t(`stages.${submission.stage}`, { ns: 'membership' })} ·{' '}
              {t('network.inbox.round', { round: submission.round })}
            </Typography>
          </Stack>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {submission.seminaryName}
            {submission.tmaiSiteAbbreviation && (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 1 }}>
                {submission.tmaiSiteAbbreviation}
              </Box>
            )}
          </Typography>
        </Box>
        <Chip
          color={isFormal ? 'primary' : 'default'}
          variant={isFormal ? 'filled' : 'outlined'}
          label={t(`network.detail.kind.${submission.submissionKind}`)}
          sx={{ fontWeight: 600, flexShrink: 0 }}
        />
      </Stack>
      <Divider sx={{ mb: 2.5 }} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
        {/* ── EL EXPEDIENTE ────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 2.5, bgcolor: 'action.hover', borderColor: 'divider' }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              {t('network.detail.evidenceAsOf', { date: fmt(p.generatedAt) })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {t('network.detail.decidesOnEvidence')}
            </Typography>
          </Paper>

          {clock && (
            <Alert severity={clock.daysRemaining < 0 ? 'error' : 'info'} sx={{ mb: 2 }}>
              {clock.daysRemaining < 0
                ? t('network.detail.candidacyExpired', {
                    date: fmt(clock.expiresAt),
                    days: Math.abs(clock.daysRemaining),
                  })
                : t('network.detail.candidacyClock', {
                    date: fmt(clock.expiresAt),
                    days: clock.daysRemaining,
                  })}
            </Alert>
          )}
          {submission.stage === 'preCandidate' && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              {t('network.detail.preCandidateAlwaysRecognized')}
            </Alert>
          )}
          {submission.submissionKind === 'legacy' && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              {t('network.detail.legacyNotice')}
            </Alert>
          )}

          {/* Los criterios, agrupados y con su ENUNCIADO. */}
          {grouped.map(({ category, items }) => (
            <Paper variant="outlined" key={category} sx={{ mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  {t(`categories.${category}`, { ns: 'membership' })}
                </Typography>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {items.filter((c) => c.status === 'met').length}/{items.length}
                </Typography>
              </Stack>
              <Divider />
              <Stack divider={<Divider />}>
                {items.map((c) => {
                  const Icon = OUTCOME_ICON[c.status] ?? HelpOutlineIcon;
                  const def = CRITERION.get(c.criterionId);
                  return (
                    <Stack
                      key={c.criterionId}
                      direction="row"
                      spacing={1.5}
                      sx={{ px: 2, py: 1.5 }}
                      alignItems="flex-start"
                    >
                      <Icon sx={{ fontSize: 20, mt: 0.25, color: OUTCOME_COLOR[c.status] }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                          {def ? def.label[lang] : c.criterionId}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                          <Typography variant="caption" sx={{ color: OUTCOME_COLOR[c.status] }}>
                            {t(`criteria.outcome.${c.status}`, {
                              ns: 'membership',
                              defaultValue: c.status,
                            })}
                          </Typography>
                          {c.gating && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('network.review.gating')}
                              sx={{ height: 18, fontSize: 11 }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
          ))}

          {/* El historial: qué se le dijo antes a esta institución. */}
          {previous.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
                {t('network.review.previousRounds')}
              </Typography>
              <Stack divider={<Divider />}>
                {previous.map((r) => (
                  <Box key={r.id} sx={{ py: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('network.inbox.round', { round: r.round })}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t(`stages.${r.stage}`, { ns: 'membership' })} · {fmt(r.submittedAt)}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(`network.inbox.status.${r.status}`, { defaultValue: r.status })}
                      />
                    </Stack>
                    {/* Lo que dijo la plataforma, traducido al leer. */}
                    {r.systemNote && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.75, color: 'text.secondary', fontStyle: 'italic' }}
                      >
                        {t(`network.systemNote.${r.systemNote.code}`, {
                          ...r.systemNote.params,
                          stage: r.systemNote.params?.['stage']
                            ? t(`stages.${r.systemNote.params['stage']}`, { ns: 'membership' })
                            : '',
                        })}
                        {r.systemNote.params?.['reason'] ? (
                          <Box component="span" sx={{ display: 'block' }}>
                            {t('network.systemNote.reason', {
                              reason: r.systemNote.params['reason'],
                            })}
                          </Box>
                        ) : null}
                      </Typography>
                    )}
                    {r.observations && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.75, color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                      >
                        {r.observations}
                      </Typography>
                    )}
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() => navigate(`/red/postulaciones/${r.id}`)}
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {t('network.review.openRound')}
                    </Link>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Box>

        {/* ── LA DECISIÓN, fija al hacer scroll ────────────────────────────── */}
        <Box
          sx={{
            width: { xs: '100%', md: wide ? 620 : 400 },
            flexShrink: 0,
            transition: 'width .18s ease',
          }}
        >
          <Paper variant="outlined" sx={{ position: { md: 'sticky' }, top: 16 }}>
            {busy && <LinearProgress />}
            <Box sx={{ p: 2.5 }}>
              {/* El tipo de trámite subió a la cabecera: es identidad del
                  expediente, no un detalle del panel de decisión. */}
              <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
                  {isFormal ? t('network.review.formalHelp') : t('network.detail.advisoryNotice')}
                </Typography>
                {/* DIRECCIONAL, sin rotar nada. El icono anterior era un
                    "desplegar" vertical girado 90°, que se leía como »« — un
                    símbolo que no dice ni hacia dónde ni qué hace. El panel crece
                    hacia la IZQUIERDA, así que la flecha apunta ahí; para volver,
                    apunta a la derecha. */}
                <Tooltip title={t(wide ? 'network.review.narrow' : 'network.review.widen')}>
                  <IconButton
                    size="small"
                    onClick={() => setWide((w) => !w)}
                    sx={{
                      display: { xs: 'none', md: 'inline-flex' },
                      mt: -0.75,
                      mr: -0.75,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary', borderColor: 'text.disabled' },
                    }}
                  >
                    {wide ? (
                      <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack spacing={0.75} sx={{ mb: 2 }}>
                <Row
                  label={t('network.review.gapsLabel')}
                  value={String(gapCount)}
                  strong={gapCount > 0}
                />
                <Row
                  label={t('network.review.noDataLabel')}
                  value={String(p.criteria.filter((c) => c.status === 'no_data').length)}
                />
                <Row label={t('network.review.totalLabel')} value={String(p.criteria.length)} />
              </Stack>

              {submission.decision && (
                <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t(`network.inbox.status.${submission.status}`)} ·{' '}
                    {fmt(submission.decision.decidedAt)}
                  </Typography>
                  {/* Una ronda cerrada por un cambio de etapa NO tiene
                      observaciones —nadie la revisó— pero sí tiene la nota de la
                      plataforma. Sin esto, el detalle quedaría mudo justo en el
                      caso que este PR vino a arreglar. */}
                  {submission.decision.systemNote && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      {t(`network.systemNote.${submission.decision.systemNote.code}`, {
                        ...submission.decision.systemNote.params,
                        stage: submission.decision.systemNote.params?.['stage']
                          ? t(`stages.${submission.decision.systemNote.params['stage']}`, {
                              ns: 'membership',
                            })
                          : '',
                      })}
                      {submission.decision.systemNote.params?.['reason'] ? (
                        <Box component="span" sx={{ display: 'block' }}>
                          {t('network.systemNote.reason', {
                            reason: submission.decision.systemNote.params['reason'],
                          })}
                        </Box>
                      ) : null}
                    </Typography>
                  )}
                  {submission.decision.observations && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {submission.decision.observations}
                    </Typography>
                  )}
                </Alert>
              )}

              {isOpen && (
                <>
                  <TextField
                    label={t('network.detail.observations')}
                    helperText={t('network.detail.observationsHelp')}
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    multiline
                    // Crece con lo escrito hasta un tope generoso: una observación
                    // útil suele ser una lista de cosas a corregir, y escribirla
                    // en cuatro líneas obliga a redactar mirando por una ranura.
                    minRows={wide ? 10 : 5}
                    maxRows={24}
                    fullWidth
                    disabled={busy}
                  />
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {/* APROBAR NO EXISTE PARA UNA CONSULTA. No está gris: no está. */}
                    {isFormal && (
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={busy}
                        onClick={() => void decide('approved')}
                      >
                        {t('network.detail.approve')}
                      </Button>
                    )}
                    <Button
                      variant={isFormal ? 'outlined' : 'contained'}
                      color="warning"
                      fullWidth
                      disabled={busy}
                      onClick={() => void decide('returned')}
                    >
                      {t('network.detail.return')}
                    </Button>
                    <Button
                      color="inherit"
                      fullWidth
                      disabled={busy}
                      onClick={() => void decide('rejected')}
                    >
                      {t('network.detail.reject')}
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: strong ? 700 : 600,
          color: strong ? 'error.main' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
